// PRODUCTION — admin flow editor
import React, { useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { CollectDataStepType } from 'src/constants/collect-data/enums'
import { isFlowEditorStepEditable } from 'src/constants/flowEditorFeatureFlags'
import {
  OnboardingTemplateStep,
  StepConfig,
  DriverLicenseStepConfig,
  BusinessEntityStepConfig,
  BackgroundCheckStepConfig,
  DotNumberStepConfig,
  McNumberStepConfig,
  SsnAndAddressStepConfig,
  VehicleAndInsuranceStepConfig,
  VehicleRegistrationStepConfig,
  InsuranceStepConfig,
  FormStepConfig,
  BaseStepConfig,
  SignContractStepConfig,
  LmsTrainingStepConfig,
} from 'src/interfaces/onboardingTemplate'
import { FormStepConfigForm } from './stepConfigForms/formStep/FormStepConfigForm'
import {
  BackgroundCheckConfigForm,
  BusinessEntityConfigForm,
  DotNumberConfigForm,
  DriverLicenseConfigForm,
  GenericStepConfigForm,
  InsuranceConfigForm,
  LmsTrainingConfigForm,
  McNumberConfigForm,
  SignContractConfigForm,
  SsnAndAddressConfigForm,
  VehicleAndInsuranceConfigForm,
  VehicleRegistrationConfigForm,
} from './stepConfigForms/simpleStepConfigForms'
import {
  getDocusignPartnerTemplates,
  getDocusignTemplateSelectOptions,
  getLmsTrainingSelectOptions,
  getLmsTrainingsPlain,
  getYardstikPackageSelectOptions,
  mergeDocusignTemplateSelectOptions,
  mergeLmsTrainingSelectOptions,
  mergeSelectOptionForCurrentValue,
  resolveDocusignTemplateSelectValue,
} from 'src/selectors/collectStepReference'
import { stepLabel } from 'src/constants/collect-data/stepTypeLabels'
import { getShowAllFields } from 'src/selectors/adminQueryFlags'
import { AdvancedStepRowFields, type AdvancedStepRowField } from './AdvancedStepRowFields'
import { flowEditorTextInputPaddedCss } from './flowEditorInputStyles'

function effectiveTemplateRowLabel(step: OnboardingTemplateStep): string {
  if (step.type === CollectDataStepType.Form) {
    return (step.config as FormStepConfig).label
  }

  if (step.type === CollectDataStepType.InsuranceCoverage) {
    return (step.config as InsuranceStepConfig).label
  }

  return step.collectDataTemplateStepLabel
}

interface Props {
  step: OnboardingTemplateStep | null
  index?: number
  total?: number
  onMove?: (direction: 'up' | 'down') => void
  onConfigChange: (config: StepConfig) => void
  onRetroactiveCollectRequiredChange: (value: boolean) => void
  onAdvancedStepRowFieldChange: (field: AdvancedStepRowField, value: boolean) => void
  onCollectDataTemplateStepLabelChange: (label: string) => void
}

export const StepConfigPanel = ({
  step,
  index,
  total,
  onMove,
  onConfigChange,
  onRetroactiveCollectRequiredChange,
  onAdvancedStepRowFieldChange,
  onCollectDataTemplateStepLabelChange,
}: Props) => {
  const showAllFields = useSelector(getShowAllFields)
  const docusignTemplateOptionsBase = useSelector(getDocusignTemplateSelectOptions)
  const docusignPartnerTemplates = useSelector(getDocusignPartnerTemplates)
  const lmsTrainingOptionsBase = useSelector(getLmsTrainingSelectOptions)
  const lmsTrainingsPlain = useSelector(getLmsTrainingsPlain)
  const yardstikPackageOptionsBase = useSelector(getYardstikPackageSelectOptions)

  const yardstikSelectOptions = useMemo(() => {
    if (!step || (step.type !== CollectDataStepType.BGC && step.type !== CollectDataStepType.MVR)) {
      return []
    }

    return mergeSelectOptionForCurrentValue(
      yardstikPackageOptionsBase,
      (step.config as BackgroundCheckStepConfig).yardstikPackage,
      'Package',
    )
  }, [step, yardstikPackageOptionsBase])

  const signContractDocusignOptions = useMemo(() => {
    if (!step || step.type !== CollectDataStepType.SignContract) {
      return []
    }

    return mergeDocusignTemplateSelectOptions(
      docusignTemplateOptionsBase,
      docusignPartnerTemplates,
      (step.config as SignContractStepConfig).docusignTemplateId,
    )
  }, [step, docusignPartnerTemplates, docusignTemplateOptionsBase])

  const lmsTrainingPickOptions = useMemo(() => {
    if (!step || step.type !== CollectDataStepType.LMSTraining) {
      return []
    }

    return mergeLmsTrainingSelectOptions(
      lmsTrainingOptionsBase,
      lmsTrainingsPlain,
      (step.config as LmsTrainingStepConfig).lmsTrainingId,
    )
  }, [step, lmsTrainingOptionsBase, lmsTrainingsPlain])

  const retroactiveExistingContractors = useMemo(
    () => ({
      checked: step?.retroactiveCollectRequired ?? false,
      onChange: onRetroactiveCollectRequiredChange,
    }),
    [step?.retroactiveCollectRequired, onRetroactiveCollectRequiredChange],
  )

  if (!step) {
    return (
      <div
        css={tw`flex min-h-[360px] items-center justify-center rounded-xl border border-shades-200 bg-shades-50 border-solid`}
      >
        <div css={tw`flex flex-col items-center gap-2.5 text-center`}>
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            css={tw`stroke-shades-300`}
          >
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
          </svg>
          <p css={tw`m-0 text-[13px] text-shades-400`}>Select a step to configure</p>
        </div>
      </div>
    )
  }

  const stepLabelText = stepLabel(step.type)

  const configReadOnly = !isFlowEditorStepEditable(step)

  const canEditTemplateRowLabel =
    !configReadOnly &&
    (step.type === CollectDataStepType.Form || step.type === CollectDataStepType.SignContract)

  const renderConfigForm = () => {
    const config = step.config
    // Each config form has typed onSave — cast through unknown since parent accepts the union type
    switch (step.type) {
      case CollectDataStepType.DriverLicense:
        return (
          <DriverLicenseConfigForm
            initialValues={config as DriverLicenseStepConfig}
            onSave={onConfigChange as unknown as (v: DriverLicenseStepConfig) => void}
            readOnly={configReadOnly}
          />
        )
      case CollectDataStepType.EIN:
        return (
          <BusinessEntityConfigForm
            initialValues={config as BusinessEntityStepConfig}
            onSave={onConfigChange as unknown as (v: BusinessEntityStepConfig) => void}
            readOnly={configReadOnly}
            retroactiveExistingContractors={retroactiveExistingContractors}
          />
        )
      case CollectDataStepType.BGC:
        return (
          <BackgroundCheckConfigForm
            initialValues={config as BackgroundCheckStepConfig}
            onSave={onConfigChange as unknown as (v: BackgroundCheckStepConfig) => void}
            yardstikPackageSelectOptions={yardstikSelectOptions}
            readOnly={configReadOnly}
            retroactiveExistingContractors={retroactiveExistingContractors}
          />
        )
      case CollectDataStepType.MVR:
        return (
          <BackgroundCheckConfigForm
            initialValues={config as unknown as BackgroundCheckStepConfig}
            onSave={onConfigChange as unknown as (v: BackgroundCheckStepConfig) => void}
            yardstikPackageSelectOptions={yardstikSelectOptions}
            readOnly={configReadOnly}
            retroactiveExistingContractors={retroactiveExistingContractors}
          />
        )
      case CollectDataStepType.SignContract: {
        const signConfig = config as SignContractStepConfig

        return (
          <SignContractConfigForm
            initialValues={{
              ...signConfig,
              docusignTemplateId: resolveDocusignTemplateSelectValue(
                docusignPartnerTemplates,
                signConfig.docusignTemplateId,
              ),
            }}
            onSave={onConfigChange as unknown as (v: SignContractStepConfig) => void}
            docusignTemplateSelectOptions={signContractDocusignOptions}
            readOnly={configReadOnly}
          />
        )
      }
      case CollectDataStepType.LMSTraining:
        return (
          <LmsTrainingConfigForm
            initialValues={config as LmsTrainingStepConfig}
            onSave={onConfigChange as unknown as (v: LmsTrainingStepConfig) => void}
            lmsTrainingSelectOptions={lmsTrainingPickOptions}
            lmsTrainings={lmsTrainingsPlain}
            readOnly={configReadOnly}
          />
        )
      case CollectDataStepType.PIICollection:
        return (
          <SsnAndAddressConfigForm
            initialValues={config as SsnAndAddressStepConfig}
            onSave={onConfigChange as unknown as (v: SsnAndAddressStepConfig) => void}
            readOnly={configReadOnly}
            retroactiveExistingContractors={retroactiveExistingContractors}
          />
        )
      case CollectDataStepType.VehicleInsurance:
        return (
          <VehicleAndInsuranceConfigForm
            initialValues={config as VehicleAndInsuranceStepConfig}
            onSave={onConfigChange as unknown as (v: VehicleAndInsuranceStepConfig) => void}
            readOnly={configReadOnly}
          />
        )
      case CollectDataStepType.InsuranceCoverage:
        return (
          <InsuranceConfigForm
            initialValues={config as InsuranceStepConfig}
            onSave={onConfigChange as unknown as (v: InsuranceStepConfig) => void}
            readOnly={configReadOnly}
          />
        )
      case CollectDataStepType.VehicleRegistration:
        return (
          <VehicleRegistrationConfigForm
            initialValues={config as VehicleRegistrationStepConfig}
            onSave={onConfigChange as unknown as (v: VehicleRegistrationStepConfig) => void}
            readOnly={configReadOnly}
          />
        )
      case CollectDataStepType.Form:
        return (
          <FormStepConfigForm
            key={step.id}
            initialValues={config as FormStepConfig}
            onSave={onConfigChange as unknown as (v: FormStepConfig) => void}
            readOnly={configReadOnly}
            retroactiveExistingContractors={retroactiveExistingContractors}
          />
        )
      case CollectDataStepType.DOTNumber:
        return (
          <DotNumberConfigForm
            initialValues={config as DotNumberStepConfig}
            onSave={onConfigChange as unknown as (v: DotNumberStepConfig) => void}
            readOnly={configReadOnly}
            retroactiveExistingContractors={retroactiveExistingContractors}
          />
        )
      case CollectDataStepType.MCNumber:
        return (
          <McNumberConfigForm
            initialValues={config as McNumberStepConfig}
            onSave={onConfigChange as unknown as (v: McNumberStepConfig) => void}
            readOnly={configReadOnly}
            retroactiveExistingContractors={retroactiveExistingContractors}
          />
        )
      default:
        return (
          <GenericStepConfigForm
            initialValues={config as BaseStepConfig}
            onSave={onConfigChange as unknown as (v: BaseStepConfig) => void}
            readOnly={configReadOnly}
          />
        )
    }
  }

  const canMoveUp = index !== undefined && index > 0
  const canMoveDown = index !== undefined && total !== undefined && index < total - 1

  return (
    <div css={tw`rounded-xl border border-shades-200 bg-shades-0 border-solid`}>
      {/* Panel header */}
      <div css={tw`flex items-start gap-2.5 px-5 pb-2 pt-4`}>
        <div css={tw`flex min-w-0 flex-1 flex-col gap-1.5`}>
          <div css={tw`flex min-w-0 items-center gap-2.5`}>
            <span
              css={tw`whitespace-nowrap rounded-md bg-warning-100 px-2.5 py-0.5 text-[11px] font-semibold text-warning-700`}
            >
              {step.type}
            </span>
            <span
              css={tw`min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[15px] font-semibold text-shades-900`}
            >
              {stepLabelText}
            </span>
          </div>
        </div>

        {/* Step order control */}
        {index !== undefined && total !== undefined && onMove && (
          <div
            css={tw`flex flex-shrink-0 items-center gap-0.5 overflow-hidden rounded-lg border border-shades-200 border-solid`}
          >
            <button
              type="button"
              onClick={() => onMove('up')}
              disabled={!canMoveUp}
              css={[
                tw`flex h-[30px] w-[30px] items-center justify-center border-0 border-r border-shades-200 bg-transparent transition-colors duration-150 border-solid`,
                canMoveUp
                  ? tw`cursor-pointer text-shades-600 hover:bg-shades-50 hover:text-shades-900`
                  : tw`cursor-not-allowed text-shades-300`,
              ]}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>
            <span
              css={tw`min-w-[44px] select-none text-center text-xs font-semibold text-shades-600`}
            >
              {index + 1} / {total}
            </span>
            <button
              type="button"
              onClick={() => onMove('down')}
              disabled={!canMoveDown}
              css={[
                tw`flex h-[30px] w-[30px] items-center justify-center border-0 border-l border-shades-200 bg-transparent transition-colors duration-150 border-solid`,
                canMoveDown
                  ? tw`cursor-pointer text-shades-600 hover:bg-shades-50 hover:text-shades-900`
                  : tw`cursor-not-allowed text-shades-300`,
              ]}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {configReadOnly && (
        <div css={tw`bg-shades-50 px-5 py-3 text-[13px] leading-snug text-shades-700`}>
          This step is loaded from the server. Only steps you add with &quot;Add step&quot; can be
          edited here.
        </div>
      )}

      <div css={tw`flex items-center gap-3 px-5 pb-2 pt-4`}>
        <label
          htmlFor="flow-editor-step-template-label"
          css={[
            tw`w-[120px] flex-shrink-0 text-xs font-semibold uppercase tracking-wide`,
            canEditTemplateRowLabel ? tw`text-shades-500` : tw`text-shades-400`,
          ]}
        >
          Label
        </label>
        <input
          id="flow-editor-step-template-label"
          type="text"
          value={effectiveTemplateRowLabel(step)}
          disabled={!canEditTemplateRowLabel}
          onChange={(e) => onCollectDataTemplateStepLabelChange(e.target.value)}
          css={[
            flowEditorTextInputPaddedCss,
            canEditTemplateRowLabel
              ? tw`cursor-text`
              : tw`cursor-not-allowed bg-shades-50 opacity-90`,
          ]}
        />
      </div>

      {renderConfigForm()}

      <AdvancedStepRowFields
        ignoreMonitoring={step.ignoreMonitoring}
        recollectSameTypeSteps={step.recollectSameTypeSteps}
        skipRetroactiveCollectIfAnySameTypeCompleted={
          step.skipRetroactiveCollectIfAnySameTypeCompleted
        }
        onFieldChange={onAdvancedStepRowFieldChange}
        fieldsEnabled={showAllFields}
        readOnly={configReadOnly}
      />
    </div>
  )
}
